import { useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { RequirementValue } from '@makyoapp/makyo-sdk/api/Organizations'
import { IUpdateProjectPrivacyBody } from '@makyoapp/makyo-sdk/api/Projects'
import { PageType } from '@makyoapp/makyo-sdk/models/AppBuilder'
import { IPluginPresenter, PluginCategories } from '@makyoapp/makyo-sdk/models/Plugins'
import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table.js'
import { ColumnAdapter } from '@makyoapp/makyo-common'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { ButtonColor, Field, Label, Modal, ModalGridFooter, SelectDropdown } from '@makyoapp/makyo-ui'

import { useGetPlugins } from '@/resolvers/plugin/query'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useVariableTextSingleCell } from '@/hooks/__project__/useVariableTextSingleCell'
import { useReqError } from '@/hooks/useReqError'
import { EVENT_KEYS } from '@/shared/custom-event'
import { AccordionItem } from '@/components/Accordion/Accordion'
import { AuthPluginSettingsModal } from '@/components/Plugins/Auth/AuthPluginSettingsModal'
import { RenderTableIcon } from '@/components/RenderTableIcon'
import { TablesDropdownV2 } from '@/components/TablesDropdownV2'
import { VariableColumnDd } from '@/components/VariableSelectV2/VariableColumnDd'

import { AuthPluginItem } from './AuthPluginItem'

const ModalUserTable = ({
  open,
  onClose,
  onSubmitUserTable,
}: {
  open: boolean
  onClose: () => void
  onSubmitUserTable: (data: Partial<IUpdateProjectPrivacyBody>) => Promise<void>
}) => {
  const { t } = useTranslation()

  // Access contexts
  const { tables, tablesMap } = useDbClientContext()
  const { project } = useAppBuilderContext()
  const printReqError = useReqError()

  const privacySettings = project?.privacy

  const defaultUserTableData = useMemo(() => {
    const userTable = Array.from(tablesMap.values()).find((tb) => tb.isUserTable)
    return {
      tableId: userTable?.id || '',
      emailColId: userTable?.userTableColumns?.email?._id || '',
      nameColId: userTable?.userTableColumns?.name?._id || '',
      photoColId: userTable?.userTableColumns?.photo?._id || '',
      roleColId: userTable?.userTableColumns?.role?._id || '',
      phoneColId: userTable?.userTableColumns?.phone?._id || '',
    }
  }, [tablesMap])

  const defaultValues = useMemo(() => {
    // check default user table
    if (!privacySettings || !privacySettings._UsersTableID) {
      return defaultUserTableData
    }
    // return from privacy settings
    return {
      tableId: privacySettings._UsersTableID || '',
      emailColId: privacySettings._EmailColID || '',
      nameColId: privacySettings._NameColID || '',
      photoColId: privacySettings._PictureColID || '',
      roleColId: privacySettings._RoleColID || '',
      phoneColId: privacySettings._PhoneColID || '',
    }
  }, [privacySettings, defaultUserTableData])

  const [userTableForm, setUserTableForm] = useState(defaultValues)
  const [loading, setLoading] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const currentUserTableName = useMemo(() => {
    return tables.find((tb) => tb.id === defaultValues.tableId)?.name || ''
  }, [tables, defaultValues.tableId])

  const newUserTableName = useMemo(() => {
    return tables.find((tb) => tb.id === userTableForm.tableId)?.name || ''
  }, [tables, userTableForm.tableId])

  // create columns options
  const columnsOptions = useMemo(() => {
    const tableColumns = tables.find((tb) => tb.id === userTableForm.tableId)?.columns || []
    return [{ id: '', name: 'Select Column' } as ColumnAdapter, ...tableColumns]
  }, [tables, userTableForm.tableId])

  const isFormValid = !userTableForm.tableId || !userTableForm.emailColId

  const handleClose = () => {
    if (loading) return
    onClose()
    setShowConfirm(false)
    setUserTableForm(defaultValues)
  }

  const handleSubmit = async () => {
    if (!privacySettings || isFormValid) return
    try {
      setLoading(true)

      const isDataChanged = JSON.stringify(userTableForm) !== JSON.stringify(defaultValues)

      await onSubmitUserTable({
        _UsersTableID: userTableForm.tableId,
        _EmailColID: userTableForm.emailColId,
        _NameColID: userTableForm.nameColId,
        _PictureColID: userTableForm.photoColId,
        _RoleColID: userTableForm.roleColId,
        _PhoneColID: userTableForm.phoneColId,
      })

      if (isDataChanged) {
        window.dispatchEvent(new CustomEvent(EVENT_KEYS.APP_AUTH_USER_TABLE_UPDATED))
      }
      toast.success(t('User table updated!'))
      handleClose()
      //
    } catch (err) {
      printReqError(err as any)
    } finally {
      setLoading(false)
    }
  }

  const handleChangeTable = (tblId: string) => {
    let updatedForm = { ...userTableForm }
    const prevTableId = userTableForm.tableId
    const newTableId = tables.find((tb) => tb.id === tblId)?.id || ''
    const newTable = tablesMap.get(newTableId)

    if (prevTableId !== newTableId) {
      //
      if (newTableId === defaultUserTableData.tableId) {
        updatedForm = { ...defaultUserTableData }
      } else {
        const defaultEmailCol = newTable?.settings._Columns.find((col) => col._Type === ColumnTypes.Email)?._id
        updatedForm = {
          tableId: newTableId,
          emailColId: defaultEmailCol || '',
          nameColId: '',
          photoColId: '',
          roleColId: '',
          phoneColId: '',
        }
      }
    }

    setUserTableForm(updatedForm)
  }

  const handleShowConfirm = () => {
    // check if the user table is changed
    if (userTableForm.tableId !== defaultValues.tableId) {
      setShowConfirm(true)
    } else {
      handleSubmit()
    }
  }

  const AlertIndicator = ({ colId }: { colId: string }) => {
    return (
      !colId && (
        <div className='absolute right-10 top-1/2 -translate-y-[55%] opacity-70'>
          <MaterialIcon icon='report' size={16} className='red' />
        </div>
      )
    )
  }

  return (
    <Modal fix show={open} onHide={handleClose} closeOnClickOutside={!loading}>
      <Modal.Header onCloseClick={onClose}>
        <div className='flex items-center gap-2 py-2'>
          <MaterialIcon icon='settings_account_box' size={16} />
          <Trans>Configure User Table</Trans>
        </div>
      </Modal.Header>

      {showConfirm ? (
        <Modal.Body noPadding className='modal-md'>
          <div className='p-4 px-6'>
            <div className='font-semibold mb-2'>
              <Trans>Are you sure you want to change the user table?</Trans>
            </div>

            <div className='mt-4 bg-amber-500/10 text-amber-400 p-3 rounded-sm border border-amber-500/20'>
              <p className='font-medium text-amber-300'>
                <Trans>Warning: This can affect existing configurations in the app!</Trans>
              </p>
              <p className='mt-2'>
                <Trans>
                  <span className='font-medium'>Profile Data</span> settings used in components, variables, or actions
                  using the previous user table may need to be manually updated.
                </Trans>
              </p>
            </div>
          </div>
          <ModalGridFooter
            onHide={() => setShowConfirm(false)}
            onCancel={() => setShowConfirm(false)}
            onSubmit={handleSubmit}
            cancelLabel={t('Cancel')}
            primaryLabel={t('Change User Table')}
            colorStyle={ButtonColor.Secondary}
            disabled={isFormValid || loading}
            isLoading={loading}
          />
        </Modal.Body>
      ) : (
        <Modal.Body noPadding className='modal-md'>
          <div className='p-4 px-6'>
            <Field>
              <Label required className='font-medium'>
                User Table
              </Label>
              <div className='flex-1'>
                <TablesDropdownV2
                  value={userTableForm.tableId}
                  onChange={handleChangeTable}
                  tables={tables}
                  menuClassName='w-[300px]'
                  showPlaceholder
                  filterOption={(tables) => {
                    return tables.filter((tb) => !tb.isSnapshot && !tb.isQuery && !tb.isSessionTable)
                  }}
                />
              </div>
            </Field>

            <hr className='my-4 bdr' />

            <Field>
              <Label required className='font-medium'>
                Email Column
              </Label>
              <VariableColumnDd
                id='auth-user-table-email-column'
                col={userTableForm.emailColId}
                setCol={(col) => setUserTableForm((prev) => ({ ...prev, emailColId: col }))}
                colOptions={columnsOptions}
                className='px-0!'
                rightSection={<AlertIndicator colId={userTableForm.emailColId} />}
              />
            </Field>

            <Field>
              <Label className='font-medium'>Name Column</Label>
              <VariableColumnDd
                id='auth-user-table-name-column'
                col={userTableForm.nameColId}
                setCol={(col) => setUserTableForm((prev) => ({ ...prev, nameColId: col }))}
                colOptions={columnsOptions}
                className='px-0!'
                rightSection={<AlertIndicator colId={userTableForm.nameColId} />}
              />
            </Field>

            <Field>
              <Label className='font-medium'>Role Column</Label>
              <VariableColumnDd
                id='auth-user-table-role-column'
                col={userTableForm.roleColId}
                setCol={(col) => setUserTableForm((prev) => ({ ...prev, roleColId: col }))}
                colOptions={columnsOptions}
                className='px-0!'
                rightSection={<AlertIndicator colId={userTableForm.roleColId} />}
              />
            </Field>

            <Field>
              <Label className='font-medium'>Phone Column</Label>
              <VariableColumnDd
                id='auth-user-table-phone-column'
                col={userTableForm.phoneColId}
                setCol={(col) => setUserTableForm((prev) => ({ ...prev, phoneColId: col }))}
                colOptions={columnsOptions}
                className='px-0!'
                rightSection={<AlertIndicator colId={userTableForm.phoneColId} />}
              />
            </Field>

            <Field>
              <Label className='font-medium'>Photo Column</Label>
              <VariableColumnDd
                id='auth-user-table-photo-column'
                col={userTableForm.photoColId}
                setCol={(col) => setUserTableForm((prev) => ({ ...prev, photoColId: col }))}
                colOptions={columnsOptions}
                className='px-0!'
                rightSection={<AlertIndicator colId={userTableForm.photoColId} />}
              />
            </Field>
          </div>

          <ModalGridFooter
            onHide={handleClose}
            onCancel={handleClose}
            onSubmit={handleShowConfirm}
            cancelLabel={t('Cancel')}
            primaryLabel={t('Save')}
            colorStyle={ButtonColor.Secondary}
            disabled={isFormValid || loading}
            isLoading={loading}
          />
        </Modal.Body>
      )}
    </Modal>
  )
}

const RenderPageOption = ({ pageLabel }: { pageLabel: any }) => {
  const pageName = useVariableTextSingleCell(pageLabel)
  return <span>{pageName}</span>
}

/**
 * PrivacyAccordionItem is a functional component that represents a privacy-related item in an accordion.
 * It provides an interactive UI item that can be expanded or collapsed to display or hide content related to privacy settings.
 */
function PrivacyAccordionItem() {
  const { t } = useTranslation()

  // Local State
  const [isLoading, setLoading] = useState(false)
  const [showUserTableModal, setShowUserTableModal] = useState(false)
  const [editedPluginSetting, setEditedPluginSetting] = useState<IPluginPresenter | null>(null)

  // Access contexts
  const { tables } = useDbClientContext()
  const { pages, project, updateProjectPrivacy } = useAppBuilderContext()
  const privacySettings = project?.privacy

  // Check current login page
  const loginPage = pages.find((p) => p.type === PageType.Login)

  const printReqError = useReqError()

  // Get plugin List
  const { data } = useGetPlugins({
    categories: [PluginCategories.Authentication],
  })
  const authPlugins = data?._Data || []
  const activePlugin = authPlugins.find((p) => p._Active)

  const redirectPageOptions = useMemo(() => {
    return pages.length > 0
      ? pages.filter((p) => !p.tableId).map((p) => ({ id: p.id, label: p.title }))
      : [
          { id: loginPage?.id || '', label: loginPage?.title || '' },
          { id: 'home-page', label: 'Home Page' },
        ]
  }, [loginPage?.id, loginPage?.title, pages])

  const defaultRedirectPageId = redirectPageOptions[0].id

  const handleUpdateAuthPlugin = async (backendValue: RequirementValue) => {
    if (!privacySettings || !activePlugin) return

    try {
      setLoading(true)

      await updateProjectPrivacy({
        _PublicAcces: privacySettings._PublicAcces,
        _PostAuthenticatePageID: privacySettings._PostAuthenticatePageID || defaultRedirectPageId,
        _PostLogoutPageID: privacySettings._PostLogoutPageID || defaultRedirectPageId,
        _PostUnauthenticatedPageID: privacySettings._PostUnauthenticatedPageID || defaultRedirectPageId,
        _PluginID: privacySettings._AuthPlugin._PluginID,
        _Backend: backendValue,
        _Frontend: {},

        // users table
        _UsersTableID: '',
        _EmailColID: '',
        _NameColID: '',
        _PictureColID: '',
        _RoleColID: '',
        _PhoneColID: '',
      })
      toast.success(t('Changes applied successfully'))
    } catch (err) {
      printReqError(err as any)
    } finally {
      setEditedPluginSetting(null)
      setLoading(false)
    }
  }

  const handleUpdatePrivacySettings = async (data: Partial<IUpdateProjectPrivacyBody>) => {
    if (!privacySettings || !activePlugin) return

    try {
      setLoading(true)

      const defaultValue: IUpdateProjectPrivacyBody = {
        _PublicAcces: privacySettings._PublicAcces,
        _PostAuthenticatePageID: privacySettings._PostAuthenticatePageID || defaultRedirectPageId,
        _PostLogoutPageID: privacySettings._PostLogoutPageID || defaultRedirectPageId,
        _PostUnauthenticatedPageID: privacySettings._PostUnauthenticatedPageID || defaultRedirectPageId,
        _PluginID: privacySettings._AuthPlugin._PluginID,
        _Backend: privacySettings._AuthPlugin._BackendRequirements,
        _Frontend: {},

        // users table
        _UsersTableID: privacySettings._UsersTableID || '',
        _EmailColID: privacySettings._EmailColID || '',
        _NameColID: privacySettings._NameColID || '',
        _PictureColID: privacySettings._PictureColID || '',
        _RoleColID: privacySettings._RoleColID || '',
        _PhoneColID: privacySettings._PhoneColID || '',
      }
      const payload = { ...defaultValue, ...data }
      console.log('payload ->', payload)

      await updateProjectPrivacy(payload)
      //
    } catch (err) {
      printReqError(err as any)
    } finally {
      setLoading(false)
    }
  }

  const handleOpenSettingModal = (pluginId: string) => {
    const plugin = authPlugins.find((p) => p._id === pluginId)
    if (plugin) {
      setEditedPluginSetting(plugin)
    }
  }

  const loginRedirectValue = useMemo(() => {
    const page = redirectPageOptions.find((p) => p.id === privacySettings?._PostAuthenticatePageID)
    if (!page) return redirectPageOptions[0]
    return {
      id: page.id,
      label: page.label,
    }
  }, [privacySettings, redirectPageOptions])

  const logoutRedirectValue = useMemo(() => {
    const page = redirectPageOptions.find((p) => p.id === privacySettings?._PostLogoutPageID)
    if (!page) return redirectPageOptions[0]
    return {
      id: page.id,
      label: page.label,
    }
  }, [privacySettings, redirectPageOptions])

  const unauthorizedPageValue = useMemo(() => {
    const page = redirectPageOptions.find((p) => p.id === privacySettings?._PostUnauthenticatedPageID)
    if (!page) return redirectPageOptions[0]
    return {
      id: page.id,
      label: page.label,
    }
  }, [privacySettings, redirectPageOptions])

  const userTableValue = useMemo(() => {
    const defaultUserTable = tables.find((t) => t.isUserTable)
    return privacySettings?._UsersTableID
      ? tables.find((t) => t.id === privacySettings?._UsersTableID)
      : defaultUserTable
    //
  }, [privacySettings, tables])

  const handleUpdateUserTable = async (data: Partial<IUpdateProjectPrivacyBody>) => {
    await handleUpdatePrivacySettings(data)
  }

  // Render
  if (!privacySettings) return null

  const isPublic = Boolean(privacySettings._PublicAcces)

  return (
    <AccordionItem
      itemId='setting-privacy'
      title={<Trans>Privacy</Trans>}
      righSection={isLoading ? <span className='loading spin' /> : null}
    >
      {/* <div className='pb-4'>
        <label className='w-full cursor-pointer flex-c'>
          <div>
            <Radio checked={isPublic} value='on' name='privacy-public' onClick={() => handleSetPublic()} />
          </div>
          <Trans>Public</Trans>
        </label>
      </div> */}

      {/* <div className='pb-4'>
        <label className='w-full cursor-pointer flex-c'>
          <div>
            <Radio
              checked={!isPublic}
              value='off'
              name='privacy-private'
              onClick={() => {
                handleSetPrivate()
              }}
            />
          </div>
          <div className='flex-c w-full justify-between'>
            <Trans>Private</Trans>
            <PremiumBadge icon={<Diamond16 />}>
              <Trans>Premium</Trans>
            </PremiumBadge>
          </div>
        </label>
      </div> */}

      {!isPublic && (
        <div className='px-1'>
          <div className='py-4'>
            <div className='font-semibold uppercase pb-2'>
              <Trans>Redirects</Trans>
            </div>

            {/* //* Hiden for now  */}
            {/* <Field>
              <label className='shrink-0 w-[90px]'>
                <Trans>Post Login</Trans>
              </label>
              <SelectDropdown
                className='w-full min-w-0'
                toggleClassName='w-full min-w-0'
                menuContainerClassName=''
                value={loginRedirectValue}
                onChange={(opt) => handleUpdatePrivacySettings({ _PostAuthenticatePageID: opt.id })}
                renderOption={(opt) => <RenderPageOption pageLabel={opt.label} />}
                options={redirectPageOptions}
              />
            </Field>

            <Field>
              <label className='shrink-0 w-[90px]'>
                <Trans>Post Logout</Trans>
              </label>
              <SelectDropdown
                className='w-full min-w-0'
                toggleClassName='w-full min-w-0'
                menuContainerClassName=''
                value={logoutRedirectValue}
                onChange={(opt) => handleUpdatePrivacySettings({ _PostLogoutPageID: opt.id })}
                renderOption={(opt) => <RenderPageOption pageLabel={opt.label} />}
                options={redirectPageOptions}
              />
            </Field> */}

            <Field isVertical>
              <label className='shrink-0'>
                <Trans>Unauthorized Pages</Trans>
              </label>
              <SelectDropdown
                className='w-full min-w-0'
                toggleClassName='w-full min-w-0'
                menuContainerClassName=''
                value={unauthorizedPageValue}
                onChange={(opt) => handleUpdatePrivacySettings({ _PostUnauthenticatedPageID: opt.id })}
                renderOption={(opt) => <RenderPageOption pageLabel={opt.label} />}
                options={redirectPageOptions}
              />
            </Field>
          </div>

          <div className='py-4'>
            <div className='font-semibold uppercase pb-4'>
              <Trans>User Table</Trans>
            </div>

            <div
              role='button'
              onClick={() => setShowUserTableModal(true)}
              className='cursor-pointer py-3 px-3 rounded-sm mk-bg-hover flex justify-between items-center gap-2˝'
            >
              <div className='flex items-center gap-2'>
                <RenderTableIcon table={userTableValue} />
                {userTableValue?.name}{' '}
                <span className='text-xs text-light'>{userTableValue?.isUserTable ? '(Default)' : ''}</span>
              </div>
              <MaterialIcon icon='settings' size={16} className='opacity-70' />
            </div>
          </div>

          <div className='py-4'>
            <div className='font-semibold uppercase pb-4'>
              <Trans>Authentication Plugin</Trans>
            </div>
            <div className='overflow-x-scroll'>
              <div className='flex gap-4'>
                {authPlugins.map((plugin) => {
                  return <AuthPluginItem key={plugin._id} plugin={plugin} onSettingClick={handleOpenSettingModal} />
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {!!editedPluginSetting && (
        <AuthPluginSettingsModal
          show={!!editedPluginSetting}
          plugin={editedPluginSetting}
          privacySettings={privacySettings}
          isLoading={isLoading}
          onClose={() => {
            setEditedPluginSetting(null)
          }}
          onSubmit={handleUpdateAuthPlugin}
        />
      )}

      <ModalUserTable
        open={showUserTableModal}
        onClose={() => setShowUserTableModal(false)}
        onSubmitUserTable={handleUpdateUserTable}
      />
    </AccordionItem>
  )
}

export default PrivacyAccordionItem
